import {
  Inject,
  Injectable
} from '@angular/core';
import {has, get, set, sortBy, isEmpty, chain, includes, filter} from 'lodash';
import moment from 'moment';
import {
  IEvent,
  IEventsDefAndSelectedState, IQueryParam,
  TimelineGroupBy
} from '@ispm-client/modules/events/Interfaces';
import {EVENT_ACTIONS} from '@ispm-client/modules/events/store/actions';
import {Store} from '@ngrx/store';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {AgregationService} from "@ispm-client/modules/aggregator/services";
import { IDefinition } from '../../../app/configuration/interfaces/definition.interface';
import { IOptellumConfig } from '../interfaces/timeline.interface';
import { NOTIFICATION_MESSAGE } from '@ispm-client/modules/aggregator/services/consts';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';

@Injectable()
export class TimelineService {
  timelineData;
  public store$: Store<IEventsDefAndSelectedState>;

  constructor(private notificationMessageService: NotificationMessageService,
              private authService: AuthenticationService,
              private notificationMessage: NOTIFICATION_MESSAGE,
              private _aggregatorService: AgregationService,
              private store: Store<IEventsDefAndSelectedState>,
              private _httpClient: HttpClient){
      this.store$ = store.select((state: any) => state.events) as any;
  }


  /**
   * Create event object with data and config
   * @param params - query params to get events and configurations
   * @return [{data: ITimelineEventData, config: ITimelineEventConfig}]
   */
  getMappedTimelineDataWithConfig(eventDefinitions?: any, params?: any) {
    const eventMappedData = [];
    let prevYear;

    // filter based on configured event categories and includeInTimeline flag
      this.timelineData = filter(this.timelineData, (item => {
          return (
            (get(params, 'category') ? includes(get(params, 'category'), get(item, 'category.code')) : true)
            && get(item, 'includeInTimeline') && (get(params, 'showOnlyApproved') ? get(item, 'approved') : true)
            && !get(item, 'archived'));
      }));

    // sort by date
    this.timelineData = sortBy(this.timelineData, ['date']);

    // group by based on configuration
    const groupBy = params['groupBy'];
    if (params['groupBy']) {
      this.timelineData = chain(this.timelineData)
        .map(data => {
          switch (groupBy) {
            case TimelineGroupBy.MONTH:
              return Object.assign({ groupBy: moment(get(data, 'date')).format('MM-YYYY')}, data);
            case TimelineGroupBy.EVENT_CATEGORY:
              return Object.assign({ groupBy: get(data, 'category.display')}, data);
            default:
              return Object.assign({ groupBy: moment(get(data, 'date')).format('ll')}, data);
          }
        })
        .groupBy('groupBy')
        .map((events, key) => {
          if (events.length === 1 && groupBy !== TimelineGroupBy.EVENT_CATEGORY) {
            return Object.assign({ }, ...events);
          } else {
            return { date: events[0]['date'], groupLabel: key, isGrouped: true, events: events};
          }
        })
        .value();
    }

    for (const timelineEvent of  this.timelineData) {


      const currentYear = new Date(timelineEvent.date).getFullYear();
      let eventObj = { };

      // create year event to display on timeline
      if (prevYear !== currentYear) {
        set(eventObj, 'data', { date: get(timelineEvent, 'date')});
        set(eventObj, 'config', this.getEventDefinition(eventDefinitions, 'year'));
        eventMappedData.push(eventObj);
        prevYear = currentYear;
      }
      if (timelineEvent.isGrouped) {
        timelineEvent.events.forEach((event, index) => {
          eventObj = { };
          set(eventObj, 'data', event);
          set(eventObj, 'config', Object.assign({ }, this.getEventDefinition(eventDefinitions, get(event, 'category.code'))));
          if (groupBy !== TimelineGroupBy.EVENT_CATEGORY) {
            set(eventObj, 'config.dateFormat', groupBy === 'month' ? 'dd EEE' : 'shortTime');
          }
          timelineEvent.events[index] = eventObj;
        });
        eventObj = { };
        set(eventObj, 'data', timelineEvent);
        set(eventObj, 'config', Object.assign({ }, this.getEventDefinition(eventDefinitions, 'group')));
        if (groupBy === TimelineGroupBy.EVENT_CATEGORY) {
          set(eventObj, 'config.groupLabel', timelineEvent.groupLabel);
        } else {
          set(eventObj, 'config.dateFormat', groupBy === TimelineGroupBy.MONTH ? 'MMM y' : 'dd MMM y');
        }
        set(eventObj, 'config.isGrouped', true);

        eventMappedData.push(eventObj);
      } else {
        eventObj = { };
        set(eventObj, 'data', timelineEvent);
        set(eventObj, 'config', this.getEventDefinition(eventDefinitions, get(timelineEvent, 'category.code')));
        eventMappedData.push(eventObj);
      }
    }
    return eventMappedData;
  }

  getEventDefinition(eventDefinitions, category: string) {
    // get event definition
    if (!isEmpty(eventDefinitions)) {
          const def = eventDefinitions.get(category);
          return (def) ? def : eventDefinitions.get('default');
    }
    return null;
  }

  /**
   * It queries the static data present in additional configuration and fetches optellum configured values
   * @returns Observable of IDefinition
   */
  getAdditionalOptellumConfiguration(): Observable<IDefinition> {
    const optellumAdditionalConfigDataBinding =  [{
      identifier: "optellumAdditionalConfig",
      targetResource: "AdditionalConfiguration",
      queryString: `_id=Optellum-launch-constants`,
      skipIdAndMeta: true,
      identifierType: "object"
    }];

    return this._aggregatorService.registerQuery(optellumAdditionalConfigDataBinding).take(1).map(bundleResult => bundleResult[0]["optellumAdditionalConfig"]);
  }

  private currentUserId(): string {
    return this.authService.user.id;
  }

  private currentUserName(): string {
    return this.authService.user.username;
  }

  /**
   * Method to get the study instance uid from the current event.
   * study instance uid is mentioned as a context item in the event.
   */
  private getCurrentStudy(event:boolean, eventData?) : string{
    let studyId = "";
    if(event){
      this.store$.select('timelineSelectedEvent').subscribe(currentEvent => {
        currentEvent.context.forEach((context) => {
          if(context.resourceType === "StudyInstanceUid" ){
            studyId = context.reference;;
            return;
          }
        })
      })
    }else{
      eventData[0].context.forEach((currentContext) => {
        if(currentContext.resourceType === "StudyInstanceUid" ){
          studyId = currentContext.reference;;
          return;
        }
      })
    }

    if(studyId === ""){
      const message = 'Not able to find study instance id from event ';
      this.notificationMessageService.error(message);
      throw new Error(message);
    }else{
      return studyId;
    }
  }

  /**
   * Method to get mapped user for a practitioner.
   * Mapping is mentioned as an array item in the identifier object.
   * @param currentPractitioner
   */
  private getContextUserID(currentPractitioner) : string{
    let contextLaunchUserId = "UTD";
    if(has(currentPractitioner,"identifier")){
      currentPractitioner.identifier.forEach((currentIdentifier) => {
        if(has(currentIdentifier,"type.text") && currentIdentifier.type.text === "DynaCADUserId"){
          contextLaunchUserId = currentIdentifier.value;
        }
      });
    }
    if(contextLaunchUserId === "UTD"){
      contextLaunchUserId = this.currentUserName();
    }
    return contextLaunchUserId;
  }

  /**
   * This method to get the URL for the assigned server.
   * This method will return default server if match is given as UTD or not passed.
   * @param servers
   * @param match
   */
  private getSuitableURL( servers = [], match?:string):string{
    let url : string ='';
    if( Array.isArray(servers)){
      servers.forEach((server) => {
        if ((match === undefined || match === "UTD") && server.default === true) {
          url = server.url;
          return;
        } else if (match === server.id) {
          url =  server.url;
          return;
        }
      });
      if(url === ""){
        const message = 'Not able to find context launch URL ';
        this.notificationMessageService.error(message);
        throw new Error(message);
      }else{
        return url;
      }
    }else{
      const message = 'Not able to find server configurations.';
      this.notificationMessageService.error(message);
      throw new Error(message);
    }


  }

  /**
   * Method to get assigned server for a practitioner.
   * @param currentPractitioner
   */
  private getAssignedServer(currentPractitioner) : string{
    let assignedServer = "UTD";
    if(has(currentPractitioner,"assignedServers")
      && currentPractitioner.assignedServers !== null ){
      assignedServer = currentPractitioner.assignedServers[0].reference;
    }
    return assignedServer;
  }

  private contextLaunchQueries(externalDataBinding?: [IQueryParam]) {
    const dataBindings = [];

    dataBindings.push(
      {
        'identifier': 'practitioner',
        'queryString': `_id=${this.currentUserId()}&_fieldslist=identifier,assignedServers`,
        'targetResource': 'Practitioner'
      },
      {
        'identifierType':'array',
        'identifier': 'Server',
        'queryString': `application=DynaCAD&_fieldslist=id,name,url,default`,
        'targetResource': 'ServerConfiguration'
      }
    );
    if(externalDataBinding !== undefined){
      dataBindings.push(...externalDataBinding);
    }
    return dataBindings;
  }

  /**
   * Modify URL with study id and user id.
   * @param currentPractitioner
   * @param url
   */
  private getModifiedURL(event:boolean, currentPractitioner, url, currentStudy:string): string{
    const obj = {
      "DynaCADUserId" : this.getContextUserID(currentPractitioner),
      "StudyInstanceUID" : currentStudy
    };
    return this._aggregatorService.interpolate(url, obj);
  }

  /**
   * Method to launch context URL.
   * This method takes assigned server from current practitioner. If not assigned then take default server.
   * User mapping from current practitioner.
   * Study id from current event.
   */
  public launchContextURL(event:boolean, dataBinding?: [IQueryParam]){
    const querySubscription = this._aggregatorService.registerQuery(this.contextLaunchQueries(dataBinding)).subscribe(bundleResult => {
      const currentPractitioner = bundleResult[0].practitioner;
      const url = this.getSuitableURL(bundleResult[1].Server, this.getAssignedServer(currentPractitioner));
      let currentStudy = "UTD";
      if(dataBinding !== undefined){
        const eventContext = get(bundleResult[2], dataBinding[0].identifier);
        currentStudy = this.getCurrentStudy(event, eventContext);
      }else{
        currentStudy = this.getCurrentStudy(event);
      }
      const modifiedURL = this.getModifiedURL(event, currentPractitioner, url, currentStudy);
      let splitArray = url.split("/", 4);
      const server = splitArray[2];
      //giving message on the study and server.
      this.notificationMessageService.warning('DynaCAD launching with server '+server+" and for the study "+ currentStudy
        +" Please check DynaCAD client configuration if some issues on context launch.");
      this.callContextURL(modifiedURL);
      if (querySubscription) {
        querySubscription.unsubscribe();
      }
    })
  }

  /**
   * This method queries the store and fetched the configured Optellum server url
   * @returns Observable of server configured url
   */
  public getOptellumServerUrl(): Observable<string> {
    return this._aggregatorService.registerQuery(this.optellumLaunchQueries()).map(bundleResult => !isEmpty(bundleResult[0].Server[0]) ? bundleResult[0].Server[0].url : "");
  }

  /**
   * Construct the data binding that queries for server configuration for a given practitioner
   */
  private optellumLaunchQueries() {
    const dataBindings = [
      {
        'identifierType':'array',
        'identifier': 'Server',
        'queryString': `application=Optellum&_fieldslist=id,name,url,default`,
        'targetResource': 'ServerConfiguration'
      }
    ];
    return dataBindings;
  }

  /**
   * It builds URL and launch the Optellum dashboard
   */
  public launchOptellumURL(url: string, optellumInfo: IOptellumConfig): void {
    this.validateKeyValue(url, this.notificationMessage.SERVER_URL_NOT_CONFIGURED);
    const obj = {
      "MRN" : this.validateKeyValue(optellumInfo['mrn'], this.notificationMessage.NOT_ABLE_TO_FIND_MRN),
      "AccessionNumber" : this.validateKeyValue(optellumInfo['accessionNumber'], this.notificationMessage.NOT_ABLE_TO_FIND_ACCESSION_NUMBER),
      "TestNumber": this.validateKeyValue(optellumInfo['testNumber'], this.notificationMessage.NOT_ABLE_TO_FIND_TEST_NUMBER),
      "AppName": this.validateKeyValue(optellumInfo['testType'], this.notificationMessage.NOT_ABLE_TO_FIND_APPLICATION_NAME),
      "EventInfo": this.validateKeyValue(optellumInfo['eventInfo'], this.notificationMessage.NOT_ABLE_TO_FIND_SOURCE_EVENT_INFORMATION)
    };
    window.location.href = this._aggregatorService.interpolate(url, obj);
  }

  /**
   * Validated the input values and notifies if there it is not empty values
   * @param input - is the value
   * @param key - is the key value that need to display in notification message
   */
  private validateKeyValue(input: string, errorMessage: string): string {
    if (isEmpty(input)) {
      this.notificationMessageService.error(errorMessage);
      throw new Error(errorMessage);
    }
    return input;
  }

  /**
   * Method to call the context URL.
   * Used _httpClient as HttpClient because of context launch in a different domain.
   *
   * @param url
   */
  private callContextURL(url) {
    //used jsonp approach to avoid CROS issues.
    this._httpClient.jsonp(url, 'callback')
      .catch(error => {
        if(has(error,"error.isTrusted")){
          const errorMessage: string = "Some error occurred while  accessing context URL";
          this.notificationMessageService.error(errorMessage)
          console.log(errorMessage+` : ${JSON.stringify(error)}`);
        }else{
          console.log(`Error getting context url : ${JSON.stringify(error)}`);
        }
      return Observable.throwError(error);
    }).subscribe(res=>{
        console.log("Response for context URL "+JSON.stringify(res));
      })
  }


  /** Set selected timeline event so that it can be viewed in report manager
   *  same can be set via timeline for viewing
   */
  public setTimelineSelectedEvent(event: IEvent) {
    this.store$.dispatch({
      type: EVENT_ACTIONS.CHANGE_TIMELINE_SELECTED_EVENT,
      payload: event
    });
  }
}
